import { useState } from 'react'
import { useI18n } from '@runtime/core/i18n/i18n'
import { useAppStore } from '@runtime/core/store/appStore'
import { applyPosterThemeSelection } from '@runtime/core/theme/applyPosterThemeSelection'
import { svgPoster } from '@runtime/core/theme/posterThemes'
import { resolveApiUrl } from '@runtime/shared/api/fetchJSON'
import type { CoachPosterRecommendation } from './coach.types'
import styles from './CoachPosterRecommendationCard.module.scss'

type CoachPosterRecommendationCardProps = {
  recommendation: CoachPosterRecommendation
  onApplied: () => void
}

export default function CoachPosterRecommendationCard({
  recommendation,
  onApplied,
}: CoachPosterRecommendationCardProps) {
  const { t } = useI18n()
  const setThemeId = useAppStore(state => state.setThemeId)
  const activeThemeId = useAppStore(state => state.themeId)
  const [applying, setApplying] = useState(false)
  const [failed, setFailed] = useState(false)
  const isApplied = activeThemeId === recommendation.themeId

  const apply = async () => {
    if (applying || isApplied) return
    setApplying(true)
    setFailed(false)
    try {
      await applyPosterThemeSelection({
        themeId: recommendation.themeId,
        accessToken: recommendation.accessToken,
        setThemeId,
      })
      onApplied()
    } catch {
      setFailed(true)
    } finally {
      setApplying(false)
    }
  }

  return (
    <article className={styles.card} data-testid="coach-poster-recommendation">
      <img
        className={styles.preview}
        src={resolveApiUrl(recommendation.previewUrl)}
        alt={t('coach.poster.previewAlt', { name: recommendation.name })}
        loading="lazy"
        onError={event => {
          event.currentTarget.src = svgPoster(recommendation.name)
        }}
      />
      <div className={styles.content}>
        <strong>{recommendation.name}</strong>
        <p>{recommendation.reason}</p>
        <button
          type="button"
          onClick={apply}
          disabled={applying || isApplied}
          aria-label={
            applying || isApplied
              ? undefined
              : t('coach.poster.applyLabel', { name: recommendation.name })
          }
        >
          {applying
            ? t('coach.poster.applying')
            : isApplied
              ? t('coach.poster.applied')
              : t('coach.poster.apply', { name: recommendation.name })}
        </button>
        {failed ? (
          <span className={styles.error} role="alert">
            {t('coach.poster.applyError')}
          </span>
        ) : null}
      </div>
    </article>
  )
}
